import { isEmptyDisplayValue } from '../FieldWrapper/isEmptyDisplayValue';
import FormFieldView from '../FormFieldView/FormFieldView';
import { getDocumentRequiredMessage } from '@utils/validation/document/requiredMessage';
import { UseFormReturn, useWatch } from 'react-hook-form';
import clsx from 'clsx';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDocumentCalc } from '@models/Forms/IForms';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import './Calc.scss';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager, hasUnsupportedRemoteFormulaData } from '@utils/managers/FormulaManager';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormMessageReserveContext } from '../../FormMessageReserveContext';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { useRegisterFieldChanges, useRunFieldChanges } from '@controls/ChangeHandlers/useChangeHandlers';
import { trackFormCalculation } from '@utils/formOperationTracker';
import DisplayField from '@molecules/DisplayField/DisplayField';
import { ValueType } from '@srm/ui/types/ValueType';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import { IPartialFormGroup } from '@srm/ui/controls/types';
import InputNumberControl from '@srm/ui/controls/InputNumberControl';
import {
    useFormulaState,
    useFormulaStateWithStatus,
    useFieldSaver,
    useClearErrorOnOptional,
    useActivated,
} from '@molecules/formbuilder/hooks';
import { loadMathJs } from '@utils/mathjsLoader';
import { useDebugPanel } from '@/context/DebugPanelContext';
import FieldFormulaTooltip from '@molecules/DebugPanel/FieldFormulaTooltip/FieldFormulaTooltip';
import { valueTypeToDatatype } from '@molecules/DebugPanel/FieldFormulaTooltip/fieldEditorUtils';
import { EnsureExternalDataSourceLoaded, extractExternalDataSourceNames } from '@/utils/externalDataSourceUtils';
import { useFormulaTracePublisher } from '@molecules/formbuilder/hooks/useFormulaTracePublisher';

export interface ICalcProps<TFieldValues extends object = object> {
    calc?: IDocumentCalc;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

/**
 * Вычисленное значение совпадает с тем, что уже в форме. Сравнение нестрогое по
 * типу осознанно: в строке таблицы или в загруженной карточке лежит «5», а формула
 * даёт 5 — величина та же, а вот запись пометила бы форму изменённой (shouldDirty
 * сравнивает значение с defaultValues) и потянула бы перерисовку с каскадом
 * зависимых формул. Из-за этого форма строки таблицы становилась изменённой сразу
 * после открытия, и переход по строкам гнал полный пайплайн применения.
 */
const isSameCalcValue = (prev: unknown, next: string | number): boolean => {
    if (prev === next) return true;
    if (prev === undefined || prev === null || prev === '') return false;
    if (typeof prev === 'object') return false;
    return typeof next === 'number' ? Number(prev) === next : String(prev) === next;
};

const Calc = <TFieldValues extends object = object>({
    calc,
    formMethods,
    setError,
    isEdit,
    isNew,
    fields,
    includedFields,
    ensureExternalDataSourceLoaded,
}: ICalcProps<TFieldValues>) => {
    const { activated } = useActivated();
    const runtime = useContext(DocumentRuntimeContext);
    const { settings: debugSettings, fieldOverrides } = useDebugPanel();
    const reserveMessage = useContext(FormMessageReserveContext);
    const fieldOverride = calc?.key ? fieldOverrides.get(calc.key) : undefined;
    const visibilityFromFormula = useFormulaState(
        calc?.visibilityRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
            trace: calc
                ? {
                      targetId: calc.id || calc.key,
                      targetKind: 'field',
                      targetName: calc.name || calc.key,
                      ruleKind: 'visibility',
                  }
                : undefined,
        },
    );
    const { value: requiredFromFormula, settled: requiredFromFormulaSettled } = useFormulaStateWithStatus(
        calc?.required,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: false,
            ensureExternalDataSourceLoaded,
            trace: calc
                ? {
                      targetId: calc.id || calc.key,
                      targetKind: 'field',
                      targetName: calc.name || calc.key,
                      ruleKind: 'required',
                  }
                : undefined,
        },
    );
    const readOnlyFromFormula = useFormulaState(calc?.readonly, fields, formMethods, isEdit, isNew, activated, {
        fallback: false,
        ensureExternalDataSourceLoaded,
        trace: calc
            ? {
                  targetId: calc.id || calc.key,
                  targetKind: 'field',
                  targetName: calc.name || calc.key,
                  ruleKind: 'readonly',
              }
            : undefined,
    });
    // Дебаг-оверрайды поля (жучок): forced-значения правил мимо формул.
    const visibility = fieldOverride?.visibility ?? visibilityFromFormula;
    const required = fieldOverride?.required ?? requiredFromFormula;
    const requiredSettled = fieldOverride?.required !== undefined || requiredFromFormulaSettled;
    const readOnly = fieldOverride?.readonly ?? readOnlyFromFormula;
    const onSaveField = useFieldSaver(fields, formMethods);
    const runFieldChanges = useRunFieldChanges();
    const fieldBinding = useMemo(() => ({ key: calc?.key ?? '' }), [calc?.key]);
    useRegisterFieldChanges(fieldBinding, calc?.changes!, calc?.key ?? '', fields, isEdit, isNew, onSaveField, setError, formMethods, undefined, Boolean(calc && visibility));
    const calcRemoteDataBlocked =
        runtime.mode === 'supportPreview' && hasUnsupportedRemoteFormulaData(calc?.formula, runtime.dataSource);

    const formulaMng = useMemo(() => {
        if (!calc?.formula || calcRemoteDataBlocked) return null;
        const manager = new FormulaManager(calc.formula, runtime.dataSource, runtime.requestCache);
        manager.Init(fields, formMethods);
        return manager;
    }, [calc?.formula, calcRemoteDataBlocked, fields, formMethods, runtime.dataSource, runtime.requestCache]);

    const typeValue = calc?.valueType !== undefined ? calc?.valueType : ValueType.Integer;
    const dataField = fields[calc?.key!];
    const idField = dataField?.name;
    const externalSourceNames = useMemo(
        () => (!isNew ? extractExternalDataSourceNames(calc?.formula) : []),
        [calc?.formula, isNew],
    );
    const [externalDataSourceReady, setExternalDataSourceReady] = useState(externalSourceNames.length === 0);
    const { publishTrace: publishCalcTrace, removeTrace: removeCalcTrace } = useFormulaTracePublisher(
        calc?.formula,
        fields,
        formulaMng,
        externalSourceNames,
        calc
            ? {
                  targetId: calc.id || calc.key,
                  targetKind: 'field',
                  targetName: calc.name || calc.key,
                  ruleKind: 'formula',
              }
            : undefined,
    );

    useEffect(() => {
        if (dataField?.index !== undefined && !includedFields.includes(dataField.index)) {
            includedFields.push(dataField.index);
        }
    }, [dataField?.index, includedFields]);

    const watchFields = useMemo(() => formulaMng?.GetWatchFields() ?? [], [formulaMng]);
    const watchFormula = useWatch({
        name: watchFields,
    });

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            setExternalDataSourceReady(true);
            return;
        }

        setExternalDataSourceReady(false);
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            return;
        }

        let canceled = false;
        Promise.all(externalSourceNames.map((name) => ensureExternalDataSourceLoaded(name))).finally(() => {
            if (!canceled) {
                setExternalDataSourceReady(true);
            }
        });

        return () => {
            canceled = true;
        };
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (!calc?.formula) {
            removeCalcTrace();
            return;
        }
        if (calcRemoteDataBlocked) {
            publishCalcTrace(
                dataField?.value,
                'fallback',
                'Session-scoped источник для серверной функции формулы не настроен.',
            );
        }
    }, [calc?.formula, calcRemoteDataBlocked, dataField?.value, publishCalcTrace, removeCalcTrace]);

    /** Пишем только изменившееся значение — см. isSameCalcValue. */
    const writeCalcValue = useCallback(
        (next: string | number) => {
            if (isSameCalcValue(formMethods.getValues(idField as never), next)) return;
            formMethods.setValue(idField as any, next as any, { shouldDirty: true });
        },
        [formMethods, idField],
    );

    const calcFormula = useCallback(
        (watchFormulaVal: any[], isEditVal: boolean, isNewVal: boolean) =>
            // отказ формулы Calc показывает сам (publishCalcTrace) и ничего не пишет
            trackFormCalculation(formMethods, async () => {
                if (!formulaMng) return;
                try {
                    // Для формул с внешними источниками данных значения берём из карты полей (getDefaultValues),
                    // т.к. внешние поля не зарегистрированы в react-hook-form и отсутствуют в watch.
                    const val =
                        externalSourceNames.length > 0
                            ? await formulaMng.EvalFormulaValues(isEditVal, isNewVal)
                            : await formulaMng.EvalFormulaValues(isEditVal, isNewVal, watchFormulaVal);
                    const traceValues = externalSourceNames.length > 0 ? undefined : watchFormulaVal;

                    if (typeValue === ValueType.Text) {
                        writeCalcValue(val);
                        publishCalcTrace(val, 'evaluated', undefined, traceValues);
                        return;
                    }

                    let valForm = +val;
                    if (calc?.floatPoints) {
                        const { round } = await loadMathJs();
                        valForm = round(valForm, calc?.floatPoints);
                    }
                    writeCalcValue(valForm);
                    publishCalcTrace(valForm, 'evaluated', undefined, traceValues);
                } catch (error: unknown) {
                    publishCalcTrace(
                        undefined,
                        'error',
                        error instanceof Error ? error.message : String(error),
                        watchFormulaVal,
                    );
                    throw error;
                }
            }),
        [calc?.floatPoints, formulaMng, typeValue, externalSourceNames, publishCalcTrace, writeCalcValue, formMethods],
    );

    useEffect(() => {
        if (externalDataSourceReady && visibility && formulaMng) {
            const hasWatchedValues =
                watchFormula.length !== 0 && !watchFormula.every((element) => element === undefined);
            if (hasWatchedValues || externalSourceNames.length > 0) {
                void calcFormula(watchFormula, isEdit, isNew).catch(console.error);
            }
        }
    }, [
        watchFormula,
        isEdit,
        isNew,
        visibility,
        formulaMng,
        calcFormula,
        externalDataSourceReady,
        externalSourceNames,
    ]);

    const onInputNumberChange = useCallback(
        async (value: number | null) => {
            await runFieldChanges(
                calc?.changes!,
                value,
                calc?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
                fieldBinding,
            );
        },
        [calc?.changes, calc?.key, fields, formMethods, isEdit, isNew, onSaveField, setError, runFieldChanges, fieldBinding],
    );

    useClearErrorOnOptional(required, requiredSettled, formMethods, dataField);

    const effectiveRequired =
        debugSettings.skipAllValidation || debugSettings.skipRequiredValidation ? false : required;

    useEffect(() => {
        if (!idField) return;
        if (debugSettings.skipAllValidation || debugSettings.skipRequiredValidation) {
            formMethods.clearErrors(idField as any);
        }
    }, [debugSettings.skipAllValidation, debugSettings.skipRequiredValidation, idField, formMethods]);

    const fieldControlType = () => {
        switch (typeValue) {
            case ValueType.Double:
                return (
                    <InputNumberControl
                        label={calc?.name}
                        tooltip={<ToolTipTitle title={calc?.title} />}
                        formGroupProps={
                            { verticalBottomAlign: calc?.verticalBottomAlign, reserveMessage } as IPartialFormGroup
                        }
                        groupBy={0}
                        separator={''}
                        readOnly={readOnly}
                        required={effectiveRequired}
                        floatPoints={calc?.floatPoints ?? 2}
                        type="number"
                        step={0.01}
                        rules={{
                            required: {
                                value: effectiveRequired ? effectiveRequired : false,
                                message: getDocumentRequiredMessage(calc ?? {}, 'calc'),
                            },
                        }}
                        name={idField}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={calc?.placeholder ? calc.placeholder : undefined}
                        testid={calc?.key}
                    />
                );
            case ValueType.Money:
                return (
                    <InputNumberControl
                        label={calc?.name}
                        tooltip={<ToolTipTitle title={calc?.title} />}
                        formGroupProps={
                            { verticalBottomAlign: calc?.verticalBottomAlign, reserveMessage } as IPartialFormGroup
                        }
                        readOnly={readOnly}
                        required={effectiveRequired}
                        floatPoints={calc?.floatPoints ?? 2}
                        step={0.01}
                        rules={{
                            required: {
                                value: effectiveRequired ? effectiveRequired : false,
                                message: getDocumentRequiredMessage(calc ?? {}, 'calc'),
                            },
                        }}
                        name={idField}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={calc?.placeholder ? calc.placeholder : undefined}
                        testid={calc?.key}
                    />
                );

            default:
                return (
                    <InputNumberControl
                        label={calc?.name}
                        tooltip={<ToolTipTitle title={calc?.title} />}
                        formGroupProps={
                            { verticalBottomAlign: calc?.verticalBottomAlign, reserveMessage } as IPartialFormGroup
                        }
                        groupBy={0}
                        separator={''}
                        rules={{
                            required: {
                                value: effectiveRequired ? effectiveRequired : false,
                                message: getDocumentRequiredMessage(calc ?? {}, 'calc'),
                            },
                        }}
                        readOnly={readOnly}
                        required={effectiveRequired}
                        name={idField}
                        type="number"
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={calc?.placeholder ? calc.placeholder : undefined}
                        testid={calc?.key}
                    />
                );
        }
    };

    const renderView = (value: any) =>
        !calc ? null : (
            <FieldWrapper
                isViewMode={!isEdit && !isNew}
                isEmpty={typeValue !== ValueType.Boolean && isEmptyDisplayValue(value)}
                inLineFormat={calc.isValue}
                label={calc.name}
                title={calc.viewTitle}
                testid={calc.key}
                verticalBottomAlign={calc?.verticalBottomAlign}
            >
                <DisplayField type={typeValue} label={calc.name} value={value} />
            </FieldWrapper>
        );

    return calc && calc.key && visibility && idField != undefined ? (
        <div
            className={clsx(
                'form-field',
                (isEdit || isNew) && calc.displayMode !== 'view' && readOnly && 'form-field--readonly',
            )}
            data-testid={calc.id ? `calc-${calc.id}` : undefined}
            data-document-field-index={dataField.index}
            {...formulaTraceAttrs(calc.id || calc.key, 'field')}
        >
            {debugSettings.showFieldFormulas && (
                <FieldFormulaTooltip
                    required={calc.required}
                    visibilityRules={calc.visibilityRules}
                    readonly={calc.readonly}
                    fieldKey={calc.key}
                    isViewMode={!isEdit && !isNew}
                    rhfPath={dataField?.name}
                    datatype={valueTypeToDatatype(typeValue)}
                    value={dataField?.value}
                    requiredResult={requiredFromFormula}
                    readonlyResult={readOnlyFromFormula}
                    visibilityResult={visibilityFromFormula}
                />
            )}
            {(isEdit || isNew) && calc.displayMode === 'view' ? (
                <FormFieldView
                    name={idField!}
                    control={formMethods.control}
                    rules={{
                        required: { value: effectiveRequired, message: getDocumentRequiredMessage(calc, 'calc') },
                    }}
                >
                    {renderView}
                </FormFieldView>
            ) : isEdit || isNew ? (
                fieldControlType()
            ) : (
                renderView(dataField?.value)
            )}
        </div>
    ) : (
        <></>
    );
};

export default Calc;
